// ============================================
// BROWSER-BASED THERMAL PRINTER UTILITY
// Optimized for EPSON TM-T81 (80mm)
// Uses Browser Print Dialog
// ============================================

import { generateReceipt } from './thermalTemplates';
import { fetchTemplatePreferences } from '../services/ClientService';

// ============================================
// 1. PRINTER PREFERENCE MANAGEMENT
// ============================================

const PRINTER_STORAGE_KEY = 'thermal_printer_selected';

export const savePrinterPort = (printerInfo) => {
    try {
        localStorage.setItem(PRINTER_STORAGE_KEY, JSON.stringify({
            ...printerInfo,
            savedAt: new Date().toISOString()
        }));
        console.log('✓ Printer preference saved');
        return true;
    } catch (e) {
        console.warn('Failed to save printer preference:', e);
        return false;
    }
};

export const hasSavedPrinter = () => {
    try {
        const saved = localStorage.getItem(PRINTER_STORAGE_KEY);
        return saved !== null;
    } catch (e) {
        return false;
    }
};

export const clearPrinterPreference = () => {
    try {
        localStorage.removeItem(PRINTER_STORAGE_KEY);
        console.log('✓ Printer preference cleared');
        return true;
    } catch (e) {
        console.warn('Failed to clear printer preference:', e);
        return false;
    }
};

// ============================================
// 2. MAIN PRINT FUNCTION - SILENT PRINTING
// ============================================

export const printToThermalPrinter = async (orderDetails) => {
    try {
        console.log('🖨️ Initiating silent thermal print...');
        
        // Fetch template preferences from backend
        let templateId = 'classic'; // Default
        let billSize = '80mm'; // Default
        
        try {
            const prefResponse = await fetchTemplatePreferences();
            if (prefResponse.success && prefResponse.data) {
                templateId = prefResponse.data.templateId || 'classic';
                billSize = prefResponse.data.billSize || '80mm';
                console.log(`✓ Using template: ${templateId}, size: ${billSize}`);
            }
        } catch (prefError) {
            console.warn('Could not load template preferences, using defaults:', prefError);
        }
        
        // Generate HTML using selected template
        const htmlContent = generateReceipt(orderDetails, templateId, billSize);
        
        // Create hidden iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.opacity = '0';
        iframe.style.pointerEvents = 'none';
        document.body.appendChild(iframe);
        
        // Write content to iframe
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        // Wait for content to load
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set print settings to auto-print to default printer
        iframe.contentWindow.focus();
        
        // Try to print silently
        try {
            // This will use the system's default printer
            iframe.contentWindow.print();
            
            console.log('✓ Print command sent to default printer');
            
            // Mark as saved so we don't show instructions again
            savePrinterPort({ timestamp: new Date().toISOString() });
            
        } catch (printError) {
            console.warn('Print error:', printError);
        }
        
        // Clean up iframe after printing
        setTimeout(() => {
            try {
                if (iframe && iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
            } catch (e) {
                console.warn('Cleanup error:', e);
            }
        }, 2000);
        
        return {
            success: true,
            method: 'Direct Print to Default Printer',
            message: 'Sent to default printer'
        };
        
    } catch (error) {
        console.error('❌ Thermal printing error:', error);
        
        return {
            success: false,
            error: error.message || 'Failed to print. Please try again.',
            originalError: error.name
        };
    }
};

// For backward compatibility
export const ThermalPrinterCommands = {};